import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SearchLocationHero from './LocationSearchHero/LocationSearchHero';
import MapCoveoResultToCard from './mapCoveoResultToCard/mapCoveoResultToCard';
import { LocationSearchCoords, SearchLocationHeroProps } from './LocationSearch.types';
import { uniqueSiteRelatedCommunities } from './uniqueSiteRelatedCommunities';
import useSitesResultList from 'lib/hooks/coveo/sitesHeadless/useSitesResultList';
import useSitesQuerySummary from 'lib/hooks/coveo/sitesHeadless/useSitesQuerySummary';
import sitesHeadlessEngine from 'lib/utils/coveo/sitesHeadless/sitesHeadlessEngine';
import { RootState } from 'src/store/store';
import {
  setCommunities,
  setCoords,
  setNationwideTotal,
  toggleCommunity,
} from '../../features/locationSearchSlice';

function LocationSearch(props: SearchLocationHeroProps) {
  const dispatch = useDispatch();
  const { coords, sortByDistance, community, nationwideTotal, communities, hasSearched } =
    useSelector((state: RootState) => state.locationSearch);

  const searchOptions = useMemo(
    () => ({
      latitude: coords?.lat,
      longitude: coords?.lng,
      sortByDistance,
      community: community ?? undefined,
    }),
    [coords, sortByDistance, community]
  );

  const { state, isLoading } = useSitesResultList(sitesHeadlessEngine, searchOptions);
  const queryTotal = useSitesQuerySummary(sitesHeadlessEngine);

  useEffect(() => {
    if (!hasSearched && queryTotal > 0) {
      dispatch(setNationwideTotal(queryTotal));
    }
  }, [dispatch, hasSearched, queryTotal]);

  useEffect(() => {
    dispatch(setCommunities(uniqueSiteRelatedCommunities(state)));
  }, [dispatch, state]);

  const handleLocationSearch = (nextCoords: LocationSearchCoords) => {
    dispatch(setCoords(nextCoords));
  };

  const handleCommunitySelect = (nextCommunity: string) => {
    dispatch(toggleCommunity(nextCommunity));
  };

  return (
    <>
      <SearchLocationHero
        {...props}
        nationwideTotal={nationwideTotal || queryTotal}
        communities={communities}
        community={community}
        onCommunitySelect={handleCommunitySelect}
        onLocationSearch={handleLocationSearch}
      />
      <MapCoveoResultToCard
        results={state}
        isLoading={isLoading}
        totalCount={queryTotal}
        fields={props.fields}
      />
    </>
  );
}

export default LocationSearch;
